/*
PURPOSE: Scoped error boundary for heavy public-landing pages so a render
fault in any section degrades to a calm in-page fallback instead of bubbling
to the router errorElement (which would collapse the page to NotFound).
USE CASES:
- Wraps Home (/ and /landing), PublicArtist (/a/:id), ArtistPayoutCollection
  (/a/:artistUrl/collect), and Thankyou (/thankyou). Any other heavy public
  page with a data-wrapper surface is a candidate.
DOMAIN BOUNDARIES:
- Presentation + error logging, plus the one-shot reload for a chunk-load failure
  (utils/chunkLoadRecovery). No data, and no routing beyond that reload.
- The fallback is self-contained (no dependency on the failing subtree or on
  shared chrome) and editorially styled so a failure still looks intentional.
*/

import React from 'react';
import logger from '../../utils/logger';
import { isChunkLoadError, recoverFromChunkLoadError } from '../../utils/chunkLoadRecovery';

interface PublicPageErrorBoundaryProps {
  children: React.ReactNode;
}

interface PublicPageErrorBoundaryState {
  hasError: boolean;
  // Render nothing while a chunk-load reload is in flight, so the fallback does not flash up first.
  awaitingReload: boolean;
}

class PublicPageErrorBoundary extends React.Component<
  PublicPageErrorBoundaryProps,
  PublicPageErrorBoundaryState
> {
  state: PublicPageErrorBoundaryState = { hasError: false, awaitingReload: false };

  static getDerivedStateFromError(error: unknown): PublicPageErrorBoundaryState {
    return { hasError: true, awaitingReload: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (recoverFromChunkLoadError(error)) return;
    if (this.state.awaitingReload) this.setState({ awaitingReload: false });
    logger.error('Public page render error', error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.awaitingReload) return null;
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 text-center">
          <h1 className="font-cormorant font-normal text-editorial-muted text-editorial-heading">
            Something went quiet.
          </h1>
          <p className="mt-4 text-editorial-body text-editorial-muted/80 max-w-prose">
            We hit a snag loading this page. Please refresh, or head back to
            explore Coral.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 mt-8 bg-coral-orange hover:bg-coral-orange/90 text-editorial-muted rounded-full px-6 py-3 text-sm font-medium transition-colors"
          >
            Back to home
          </a>
        </div>
      );
    }

    return this.props.children;
  }
}

export default PublicPageErrorBoundary;
